import React from "react";
import { BrowserRouter, Routes, Route, Navigate, Link } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";
import { FavoritesProvider } from "./context/FavoritesContext";
import Nav from "./components/Nav";
import ProtectedRoute from "./components/ProtectedRoute";
import InstallPrompt from "./components/InstallPrompt";
import AmbientSound from "./components/AmbientSound";
import VisitCounter from "./components/VisitCounter";
import R2A2Chat from "./components/R2A2Chat";
import LaunchTicker from "./components/LaunchTicker";
import ClickSound from "./components/ClickSound";
import TermsGate from "./components/TermsGate";
import BootSequence from "./components/BootSequence";
import InteractiveEffects from "./components/InteractiveEffects";
import FissionField from "./components/FissionField";
import SupportPromoModal from "./components/SupportPromoModal";

import NewsPage from "./pages/NewsPage";
import LaunchesPage from "./pages/LaunchesPage";
import LaunchDetailPage from "./pages/LaunchDetailPage";
import EventsPage from "./pages/EventsPage";
import HistoryPage from "./pages/HistoryPage";
import HistoryAllPage from "./pages/HistoryAllPage";
import FleetPage from "./pages/FleetPage";
import RocketDetailPage from "./pages/RocketDetailPage";
import ScanPage from "./pages/ScanPage";
import PilotPage from "./pages/PilotPage";
import ArchivePage from "./pages/ArchivePage";
import ComparePage from "./pages/ComparePage";
import AuthPage from "./pages/AuthPage";
import RosterPage from "./pages/RosterPage";
import AstronautDetailPage from "./pages/AstronautDetailPage";
import SpacewalkDetailPage from "./pages/SpacewalkDetailPage";
import StatsPage from "./pages/StatsPage";
import AdminPage from "./pages/AdminPage";
import CommunityListPage from "./pages/CommunityListPage";
import CommunityDetailPage from "./pages/CommunityDetailPage";
import TermsPage from "./pages/TermsPage";

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <FavoritesProvider>
          <div className="sat-stars" aria-hidden />
          <FissionField />
          <div className="sat-waves" aria-hidden>
            {[0,1,2,3,4,5,6].map((i) => (
              <div key={i} className={`sat-waves__band sat-waves__band--${i}`} />
            ))}
          </div>
          <div className="sat-dna" aria-hidden />
          <div className="sat-scanline" aria-hidden />
          <div className="relative z-10 min-h-screen flex flex-col">
            <Nav />
            <LaunchTicker />
            <main className="flex-1 max-w-[1400px] w-full mx-auto px-4 lg:px-8 py-6">
              <Routes>
                <Route path="/" element={<Navigate to="/news" replace />} />
                <Route path="/news" element={<NewsPage />} />
                <Route path="/launches" element={<LaunchesPage />} />
                <Route path="/launches/:id" element={<LaunchDetailPage />} />
                <Route path="/history" element={<HistoryPage />} />
                <Route path="/history/all" element={<HistoryAllPage />} />
                <Route path="/events" element={<EventsPage />} />
                <Route path="/fleet" element={<FleetPage />} />
                <Route path="/fleet/:id" element={<RocketDetailPage />} />
                <Route path="/roster" element={<RosterPage />} />
                <Route path="/roster/astronaut/:id" element={<AstronautDetailPage />} />
                <Route path="/roster/spacewalk/:id" element={<SpacewalkDetailPage />} />
                <Route path="/scan" element={<ScanPage />} />
                <Route path="/stats" element={<StatsPage />} />
                <Route path="/community" element={<CommunityListPage />} />
                <Route path="/community/:id" element={<CommunityDetailPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/auth" element={<AuthPage />} />
                <Route path="/pilot" element={<ProtectedRoute><PilotPage /></ProtectedRoute>} />
                <Route path="/pilot/archive" element={<ProtectedRoute><ArchivePage /></ProtectedRoute>} />
                <Route path="/pilot/compare" element={<ProtectedRoute><ComparePage /></ProtectedRoute>} />
                <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
                <Route path="*" element={<Navigate to="/news" replace />} />
              </Routes>
            </main>
            <footer className="border-t border-[color:var(--sat-border)] py-4">
              <div className="max-w-[1400px] mx-auto px-4 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-2 sat-label">
                <div className="flex items-center gap-3 flex-wrap justify-center md:justify-start">
                  <span>ASTRONATLAS TRANSMISSIONS · DATA VIA THE SPACE DEVS + SPACEFLIGHT NEWS API</span>
                  <span>·</span>
                  <Link to="/terms" className="hover:sat-cyan" data-testid="footer-terms">TERMS &amp; CONDITIONS</Link>
                </div>
                <VisitCounter />
              </div>
            </footer>
            <InstallPrompt />
            <AmbientSound />
            <R2A2Chat />
            <ClickSound />
            <TermsGate />
            <BootSequence />
            <InteractiveEffects />
            <SupportPromoModal />
          </div>
          <Toaster theme="dark" position="bottom-right" toastOptions={{
            style: { background: "var(--sat-surface)", border: "1px solid var(--sat-border-bright)", color: "var(--sat-text)", fontFamily: "JetBrains Mono, monospace", fontSize: "11px", letterSpacing: "0.1em", textTransform: "uppercase", borderRadius: 0 }
          }} />
        </FavoritesProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
